/* eslint-disable react/sort-comp */

/**
 * Video Playlist Factory Component.
 *
 * This class serves as a base class for JS components that need the functions
 * of a Video Playlist. For example, the Video Center uses it and extends some
 * of it's methods for component specific functionality.
 */

import { Component } from 'js-component-framework';
import clamp from 'utils/clamp';
import isMobile from 'utils/isMobile';
import 'slick-carousel';
import 'slick-carousel/slick/slick.scss';
import 'slick-carousel/slick/slick-theme.scss';
import '../article-list-carousel/article-list-carousel.scss';
import triggerIfVisible from '../../js/utils/triggerIfVisible';

const { loadAnvato } = window;

export default class VideoPlaylistFactory extends Component {
  /**
   * Whether to play videos from the playlist as soon as they're loaded.
   *
   * @type {Boolean}
   */
  autoplayNextVideo = false;

  /**
   * The ID of the DOM element where the player for the playlist is placed.
   *
   * @type {String}
   */
  pInstance = '';

  /**
   * The identifier that associates a collection of videos.
   *
   * @type {String}
   */
  playlist = '';

  /**
   * Index of the currently playing video in playlist.
   *
   * @type {Number}
   */
  playingIndex = 0;

  /**
   * The volume level of the player.
   *
   * @type {Number}
   */
  volumeLevel = 0;

  /**
   * The ID of the video that is used for OVP player
   *
   * @type {String}
   */
  videoId = ''

  /**
   * Updates title under VideoPlayer for specific layouts.
   */
  static updateHeroVideoPlayerTitle(newTitle) {
    let videoPlayerTitle;
    let videoPlayerTitlePrefix = '';
    const videoPlayerLayout = document.querySelector('.video-player--hero');

    if ('daily_news_video' === videoPlayerLayout?.dataset?.layout) {
      // eslint-disable-next-line max-len
      videoPlayerTitle = document.querySelector('#daily-news-video .video-playlist__player .video-header .video-title__text');
    } else if ('big_story_video' === videoPlayerLayout?.dataset?.layout) {
      // eslint-disable-next-line max-len
      videoPlayerTitle = document.querySelector('#big-story-video .video-playlist__player .video-header .video-title__text');
      videoPlayerTitlePrefix = 'ON NOW: ';
    } else if ('big_story_video_new' === videoPlayerLayout?.dataset?.layout) {
      // eslint-disable-next-line max-len
      videoPlayerTitle = document.querySelector('#big-story-video-new .video-playlist__player .video-header .video-title__text');
      videoPlayerTitlePrefix = 'ON NOW: ';
    } else if ('daily_news_main_video' === videoPlayerLayout?.dataset?.layout) {
      // eslint-disable-next-line max-len
      videoPlayerTitle = document.querySelector('#daily-news-main-video .video-playlist__player .video-header .video-title__text');
      videoPlayerTitlePrefix = 'ON NOW: ';
    }

    if (videoPlayerTitle) {
      videoPlayerTitle.textContent = videoPlayerTitlePrefix.concat(newTitle);
    }

    if ('daily_news_main_video' === videoPlayerLayout?.dataset?.layout) {
      // eslint-disable-next-line max-len
      videoPlayerTitle.textContent = videoPlayerTitle.textContent.replace(/^(.{55}[^\s]*).*/, '$1…');
    }
  }

  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    // Elements.
    const { articleTitles, playerEl, playerWrapper } = this.children;
    this.truncateArticleTitles = this.truncateArticleTitles.bind(this);
    this.pInstance = playerEl ? playerEl.id : '';

    // eslint-disable-next-line max-len
    const videoCenterElement = document.querySelector('.video-center-content[data-component="videoCenter"]');

    // eslint-disable-next-line max-len
    const autoplayUnmuted = videoCenterElement ? '1' === videoCenterElement.getAttribute('data-autoplay-unmuted') : false;

    // eslint-disable-next-line max-len
    const hasInteracted = '1' === sessionStorage.getItem('video_user_interacted');
    if (autoplayUnmuted && hasInteracted) {
      this.volumeLevel = 80;
    }

    this.truncateArticleTitles(articleTitles);

    if (this.pInstance) {
      this.initPlayer();
      this.setPlaylistOnPlayer(playerWrapper.dataset.playlist);
    }
  }

  /**
   * Initialize the video player.
   */
  initPlayer() {
    // Elements.
    this.onVideoClick = this.onVideoClick.bind(this);
    this.onVideoStart = this.onVideoStart.bind(this);
    this.onVideoPause = this.onVideoPause.bind(this);
    this.onVideoResume = this.onVideoResume.bind(this);
    this.onVideoEnd = this.onVideoEnd.bind(this);
    this.setCurrentVideo = this.setCurrentVideo.bind(this);
    this.advanceToSlide = this.advanceToSlide.bind(this);
    this.handleOVPEvents = this.handleOVPEvents.bind(this);
    this.onVolumeChange = this.onVolumeChange.bind(this);
    this.setPlaylistOnPlayer = this.setPlaylistOnPlayer.bind(this);

    // Events.
    window.addEventListener('nsAnvatoPlayerEvent', this.onVideoStart);
    window.addEventListener('nsAnvatoPlayerEvent', this.onVideoPause);
    window.addEventListener('nsAnvatoPlayerEvent', this.onVideoResume);
    window.addEventListener('nsAnvatoPlayerEvent', this.onVideoEnd);

    // OVP Events.
    window.addEventListener('OvpVideoEvent', this.onVideoEnd);
    window.addEventListener('OVPPlayerEvent', this.handleOVPEvents);

    Array.prototype.forEach.call(this.children.articles, (article) => {
      article.addEventListener('click', this.onVideoClick);
    });

    // Initialize the player by "navigating" to the first slide.
    this.advanceToSlide(0, false);
  }

  /**
   * Stuff to do when clicking on a video thumbnail.
   */
  onVideoClick(event) {
    const { playerWrapper } = this.children;
    const { index, collection: playlist } = event.currentTarget.dataset;

    // Set the playlist
    if (playlist) {
      this.setPlaylistOnPlayer(playlist);
    }

    // Set to new video
    if (index) {
      // We need index as an integer from here on.
      this.advanceToSlide(parseInt(index, 10), true);
    }

    // Scroll the entire video player into view with a little breathing room
    // above the player. Note that this does not account for the WP admin bar
    // for logged-in users.
    // TODO: abstract out / move to own module
    // eslint-disable-next-line max-len
    const playerYCoordinate = playerWrapper.getBoundingClientRect().top + window.pageYOffset;
    const playerHeader = playerWrapper.offsetTop;
    const playerYOffset = document.getElementById('masthead').offsetHeight;
    // eslint-disable-next-line max-len
    const isVideoHeroItem = (event && 'undefined' !== event.currentTarget.className)
      ? event.currentTarget.className.includes('hero-video-item') ?? false
      : false;

    if (isVideoHeroItem) {
      try {
        // eslint-disable-next-line max-len
        const newTitle = JSON.parse(event.currentTarget.dataset.anvatoParams).ns.title;
        VideoPlaylistFactory.updateHeroVideoPlayerTitle(newTitle);
      } catch (e) {
        // Nothing
      }
    } else {
      window.scrollTo({
        top: playerYCoordinate - playerYOffset - playerHeader,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Handle OVP events.
   * @param {Object} event
   */
  handleOVPEvents(event) {
    const { eventName, playerId } = event.detail || {};
    const { playerWrapper } = this.children || {};

    if (! playerWrapper?.querySelector(`[id="${playerId}"]`)) {
      return;
    }

    if ('volumeChange' === eventName) {
      this.onVolumeChange(event);
      return;
    }

    if ('playerUpdate' === eventName || 'titleUpdated' === eventName) {
      const { args } = event.detail || {};
      const title = args?.title || args?.videoTitle;
      if (title) {
        VideoPlaylistFactory.updateHeroVideoPlayerTitle(title);
      }
    }
  }

  /**
   * Keep track of volume level.
   * @param {Object} event
   */
  onVolumeChange(event) {
    const { args } = event.detail || {};
    this.volumeLevel = args?.volume;
  }

  /**
   * Stuff to do when current video has ended.
   */
  onVideoEnd(event) {
    const {
      name, pInstance, videoId, playerId,
    } = event.detail || {};
    const { playerWrapper } = this.children || {};
    const { useOvp } = playerWrapper.dataset || {};

    // Determine end execution condition according to use ovp player site setting toggle.
    if (parseInt(useOvp, 10)) {
      if ('VIDEO_COMPLETED' !== name
      || videoId?.toString() !== this.videoId || ! playerWrapper?.querySelector(`[id="${playerId}"]`)) { // eslint-disable-line max-len
        return;
      }
    } else if (('VIDEO_COMPLETED' !== name // We're listening to our own proxy event, not the original Anvato event.
      || pInstance !== this.pInstance)) return;

    const nextIndex = this.playingIndex + 1;

    if (this.hasMediaIconLabel(this.playingIndex)) {
      this.setLabelDefault();
    }

    this.advanceToSlide(nextIndex, false);
  }

  /**
   * Stuff to do when a video has started.
   */
  onVideoStart(event) {
    const { name, pInstance } = event.detail;

    if ('PLAYING_START' !== name || pInstance !== this.pInstance) {
      return;
    }

    const { upNextClass, playingClass } = this.options;
    const {
      up_next: upNextText,
      now_playing: nowPlayingText,
    } = window.nexstar.template_variables.l10n;
    this.setLabel(this.playingIndex + 1, upNextClass, upNextText);
    this.setLabel(this.playingIndex, playingClass, nowPlayingText);
  }

  /**
   * Stuff to do when a video is paused by the user.
   */
  onVideoPause(event) {
    const { name, pInstance } = event.detail;
    const { playingClass } = this.options;

    if ('USER_PAUSE' !== name || pInstance !== this.pInstance) {
      return;
    }

    this.setLabel(null, playingClass, '');
  }

  /**
   * Stuff to do when a video is resumed by the user.
   */
  onVideoResume(event) {
    const { name, pInstance } = event.detail;
    const { playingClass } = this.options;

    if ('USER_RESUME' !== name || pInstance !== this.pInstance) {
      return;
    }

    const {
      now_playing: nowPlayingText,
    } = window.nexstar.template_variables.l10n;
    this.setLabel(this.playingIndex, playingClass, nowPlayingText);
  }

  /**
   * Move to a different "slide" in the playlist -- that is, a different video.
   *
   * @param {Number} index Slide index.
   * @param {Boolean} isManuallyTriggered Indicates whether the slide change was triggered by a user action.
   */
  advanceToSlide(index, isManuallyTriggered) {
    const { articleContent } = this.children;

    const slide = articleContent.querySelector(
      `.article-list__article[data-index="${index}"]`,
    );
    const isVideoHeroItem = (slide && 'undefined' !== slide.className)
      ? slide.className.includes('hero-video-item') ?? false
      : false;

    const videoPlayerLayout = document.querySelector('.video-player--hero');
    // eslint-disable-next-line max-len
    const isDailyNewsVideo = 'daily_news_video' === videoPlayerLayout?.dataset?.layout;
    // eslint-disable-next-line max-len
    const isAlternateLayout = videoPlayerLayout?.classList?.contains('alternate-layout') ?? false;

    if (slide) {
      try {
        // Find the video in the parent element matching the requested index and load the attached JSON.
        const params = JSON.parse(slide.dataset.anvatoParams);
        if (isVideoHeroItem) {
          const newTitle = params.ns.title;
          VideoPlaylistFactory.updateHeroVideoPlayerTitle(newTitle);
        }

        // eslint-disable-next-line max-len
        const playManually = isDailyNewsVideo && isMobile() && isAlternateLayout;
        // If it's the first slide and autoplay is disabled, set is_manually_triggered to true.
        if (0 === index && (! params.autoplay || playManually)) {
          params.is_manually_triggered = true;
          params.autoplay = false;
        } else {
          params.is_manually_triggered = isManuallyTriggered;
        }
        this.setCurrentVideo(params, index);
      } catch (e) {
        // Nothing.
      }
    }
  }

  /**
   * Set the player to a new video.
   *
   * @param {Object} videoJson  Anvato player configuration JSON.
   * @param {Number} videoIndex Index of video in the playlist.
   */
  setCurrentVideo(videoJson, videoIndex) {
    const json = { ...videoJson };
    const { playerWrapper, articles } = this.children;
    const {
      useOvp,
      sticky,
      minimized,
      widgetName,
    } = playerWrapper.dataset || {};
    json.pInstance = this.pInstance;

    // Update the player wrapper div element with the video index
    playerWrapper.dataset.videoPlaylistIndex = videoIndex;
    // Update the playing index with current video index.
    this.playingIndex = videoIndex;

    // Getting the player widgetName
    let playerWidgetName = widgetName;
    const wrapperParent = playerWrapper.parentNode;
    const wrapperGrandparent = wrapperParent.parentNode;
    if (! playerWidgetName) {
      const parentWidgetName = wrapperParent.dataset.widgetName;
      const grandparentWidgetName = wrapperGrandparent.dataset.widgetName;
      playerWidgetName = parentWidgetName || grandparentWidgetName;
    }
    json.widgetName = playerWidgetName;

    /**
     * Autoplay the video if so instructed by the JSON; otherwise, depending on
     * whether videos are already playing.
     */
    if (! json.autoplay) {
      json.autoplay = this.autoplayNextVideo;
    }

    // For video center page.
    // eslint-disable-next-line max-len
    const activeTabArticles = document.querySelectorAll('.article-list-tabs__panels [aria-hidden=false] .article-list__article');

    if (true === videoJson?.recommendations) {
      json.recommendations = true;
    // eslint-disable-next-line max-len
    } else if (videoIndex !== articles?.length - 1 && videoIndex !== activeTabArticles?.length - 1) {
      json.recommendations = false;
    }

    json.volume = this.volumeLevel;

    /**
     * Set the JSON on the player based on type.
     */
    const setJson = () => {
      if (parseInt(useOvp, 10)) {
        this.videoId = json.video.toString();
        // Using data-react-component here is what pulls in the VideoPlayer component.
        // The other data attributes send props into the component.
        // eslint-disable-next-line max-len
        const innerHTML = `<div data-react-component="VideoPlayer"  data-video_id="${json.video}" data-video_params="${JSON.stringify(json).replaceAll('"', '\'/')}" data-sticky="${sticky}" data-minimized="${minimized}" data-allow_render="1"></div>`;

        // json value is determined in advanceToSlide function and anvatoParams attribute in dataset
        // is escaped on server side by using php esc_attr function
        // eslint-disable-next-line max-len
        document.querySelector(`[id="${json.pInstance}"]`).innerHTML = innerHTML; // phpcs:ignore

        // trigger new Event after OVP Player added to DOM as a react component.
        const event = new Event('ReactComponentAdded');
        document.dispatchEvent(event);
      } else {
        loadAnvato(json);
      }
    };

    if (0 === videoIndex) {
      triggerIfVisible(`#${json.pInstance}`, 'video-placeholder', () => {
        setJson();
      });
    } else if (0 < videoIndex) {
      setJson();
    }

    /**
     * We don't want the video to play as soon as the page loads, so this value
     * begins as false. However, if the user clicks on any video in the playlist
     * from now on, we want it to load immediately. Additionally, after a video
     * ends, we want to autoplay the next one.
     */
    this.autoplayNextVideo = true;
  }

  /**
   * Set the video thumbnail label to indicate status.
   *
   * @param { integer } index index of the playing video thumbnail.
   * @param { string } className class text indicating status.
   * @param { string } text text to be inserted into label indicating status.
   */
  setLabel(index, className, text) {
    if (! this.hasMediaIconLabel(index)) {
      return;
    }

    const { articleContent } = this.children;
    const { video: videoText } = window.nexstar.template_variables.l10n;
    const { iconTextClass } = this.options;

    const currentThumb = articleContent.querySelector(`.${className}`);
    const newThumb = articleContent.querySelector(`[data-index="${index}"]`);
    let currentThumbLabel;
    let newThumbLabel;

    if (currentThumb) {
      currentThumbLabel = currentThumb.querySelector(`.${iconTextClass}`);
    }

    if (newThumb) {
      newThumbLabel = newThumb.querySelector(`.${iconTextClass}`);
    }

    if (currentThumb && currentThumbLabel && (! text || null === index)) {
      currentThumb.classList.remove(className);
      currentThumbLabel.innerText = videoText;
      return;
    }

    if (currentThumb && currentThumbLabel) {
      currentThumb.classList.remove(className);
      currentThumbLabel.innerText = videoText;
    }

    if (newThumb && newThumbLabel) {
      newThumb.classList.add(className);
      newThumbLabel.innerText = text;
    }
  }

  /**
   * Set the video thumbnail label to default.
   */
  setLabelDefault() {
    const { upNextClass, playingClass } = this.options;
    this.setLabel(null, upNextClass, '');
    this.setLabel(null, playingClass, '');
  }

  /**
   * Set the current playlist to player wrapper.
   */
  setPlaylistOnPlayer(id) {
    if (id === this.playlist) {
      // No need to set anything
      return;
    }
    const { playerWrapper } = this.children;

    playerWrapper.dataset.playlist = id;
    this.playlist = id;
  }

  /**
   * Set the carousel arrow position to vertically align with the thumbnail.
   */
  setCarouselArrowPosition() {
    const { articleContent } = this.children;
    const $thumbnail = $(articleContent)
      .find('.article-list__article-thumb')
      .first();
    const $arrows = $(articleContent).find('.slick-arrow');

    if (! $thumbnail || ! $arrows) {
      return;
    }

    const arrowHeight = 40;
    const offsetHeight = $thumbnail.height() + arrowHeight;
    Array.prototype.forEach.call($arrows, (arrow) => {
      // eslint-disable-next-line no-param-reassign
      arrow.style.top = `${offsetHeight / 2}px`;
    });
  }

  /**
   * Truncate article titles with an ellipsis.
   */
  truncateArticleTitles() {
    const { articleTitles } = this.children;

    if (! articleTitles) {
      return;
    }

    Array.prototype.forEach.call(articleTitles, (title) => {
      if (! title) {
        return;
      }
      clamp(title, { clamp: 3 });
    });
  }

  /**
   * Determine if the current video thumbnail has a media icon label to update.
   *
   * @param videoIndex index of video in playlist.
   * @return {boolean}
   */
  hasMediaIconLabel(videoIndex) {
    const { articleContent } = this.children;
    const { iconTextClass } = this.options;
    const video = articleContent.querySelector(`[data-index="${videoIndex}"]`);

    if (! video) {
      return false;
    }

    return Boolean(video.querySelector(`.${iconTextClass}`));
  }
}
